import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from 'react-query';
import { requestsAPI, adminAPI } from '../../../services/api';
import { hydrateCartonsFromRequest, hydratePalletsFromRequest } from '../../../utils/asnEditHydration';
import ShippingForm from '../../../components/forms/ShippingForm';
import IncomingInventoryForm from '../../../components/forms/IncomingInventoryForm';
import CrossDockForm from '../../../components/forms/CrossDockForm';
import PickPackForm from '../../../components/forms/PickPackForm';
import KitBuildForm from '../../../components/forms/KitBuildForm';
import ManifestEditForm from '../../../components/forms/ManifestEditForm';
import StepProgress from '../../../components/StepProgress';
import { FiBox, FiTruck, FiPackage, FiTool } from 'react-icons/fi';
import { LuBox } from 'react-icons/lu';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';

const formatShippingMethod = (method, fallback = 'Not specified') => {
  const labels = {
    courier: 'Courier',
    freight: 'Freight',
    pickup: 'Pickup'
  };
  return labels[method] || fallback;
};

const unwrapRequestResponse = (response) => response?.request || response?.data || response;
const getRequestType = (request) => request?.requestType || request?.request_type || '';

const AdminCreateRequest = ({ isEditing = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [selectedType, setSelectedType] = useState('');
  const [step, setStep] = useState(1);
  
  // Check for pre-selected request type from URL
  const urlParams = new URLSearchParams(location.search);
  const preSelectedType = urlParams.get('type');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(true);
  const [submitError, setSubmitError] = useState(null);
  const [processedCompanies, setProcessedCompanies] = useState([]);
  const [isLoadingRequest, setIsLoadingRequest] = useState(isEditing);
  const [loadingError, setLoadingError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDestinationWarehouseName, setSelectedDestinationWarehouseName] = useState('');
  const [asnValidationFunction, setAsnValidationFunction] = useState(null);
  
  // Check if we have a valid ID for editing - more robust validation
  const hasValidId = !!(id && 
                       typeof id === 'string' && 
                       id !== 'undefined' && 
                       id !== 'null' && 
                       id !== 'new' && 
                       id !== 'create' && 
                       id.trim() !== '' && 
                       id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)); // UUID format

  // Handle pre-selected request type from URL and state from navigation
  useEffect(() => {
    if (preSelectedType && !isEditing) {
      setSelectedType(preSelectedType);
      formik.setFieldValue('requestType', preSelectedType);
      // Keep step at 1 to show company & type selection for better UX
    }
    
    // Handle returning from shipping options page
    if (location.state?.shipmentData && location.state?.step) {
      const { shipmentData, step: returnStep, returnFromShipping } = location.state;

      // Restore form data - convert scheduledDate back to Date object (serialized as string through location.state)
      const restoredData = {
        ...shipmentData,
        scheduledDate: shipmentData.scheduledDate ? new Date(shipmentData.scheduledDate) : null
      };
      formik.setValues(restoredData);
      setSelectedType(shipmentData.requestType);

      // Restore selectedCompany from shipment data if available
      if (shipmentData._selectedCompany) {
        setSelectedCompany(shipmentData._selectedCompany);
      }
      
      // Set step to 4 (Review) if returning from shipping options
      if (returnFromShipping) {
        setStep(4);
      } else {
        setStep(returnStep);
      }
    }
  }, [preSelectedType, isEditing, location.state]);

  // Fetch existing request if editing (only when we have a valid ID)
  const { data: existingRequest, isLoading: isLoadingExistingRequest, error: existingRequestError } = useQuery(
    ['request', id],
    async () => unwrapRequestResponse(await adminAPI.getRequest(id)),
    {
      enabled: isEditing === true && hasValidId === true, // Explicit boolean checks
      onSuccess: (data) => {
        setIsLoadingRequest(false);
      },
      onError: (err) => {
        console.error('Error fetching request:', err);
        setLoadingError(err.message || 'Failed to load request');
        setIsLoadingRequest(false);
      }
    }
  );
  
  // Fetch companies for selection with search filter
  const { data: companiesData, isLoading: isLoadingCompanies } = useQuery(
    ['admin-customer-search', companySearchTerm],
    () => adminAPI.searchCustomers(companySearchTerm),
    {
      // Only run the selector search after enough input to avoid showing all customers.
      enabled: companySearchTerm.trim().length >= 3,
      keepPreviousData: true,
      staleTime: 5000 // Keep data fresh for 5 seconds
    }
  );
  
  // Process company data regardless of the API response structure
  useEffect(() => {
    if (companiesData) {
      if (Array.isArray(companiesData.customers) && companiesData.customers.length > 0) {
        setProcessedCompanies(companiesData.customers);
      } else if (Array.isArray(companiesData.data) && companiesData.data.length > 0) {
        setProcessedCompanies(companiesData.data);
      } else {
        setProcessedCompanies([]);
      }
    } else {
      setProcessedCompanies([]);
    }
  }, [companiesData]);
  
  // Create the form with Formik
  const formik = useFormik({
    initialValues: {
      requestType: '',
      companyId: '',
      scheduledDate: null,
      lineItems: [],
      specialInstructions: '',
      
      // Ship-specific fields
      destinationAddress: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      shippingMethod: '',
      liftgateRequired: false,
      whiteGlove: false,
      shipmentGroups: [],

      // Kit build specific fields
      kitName: '',
      kitQuantity: 1,
      qcChecklist: [],
      
      // Incoming inventory specific fields
      sourceAddress: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      reference_number: '',
      vendor: '',
      pallets: [],
      cartons: [],
      destinationWarehouseId: '',

      // Default receiving instructions for ASN / manifest requests. The
      // customer-side form pre-populates these from the company's
      // configured defaults; the admin side mirrors that behavior in
      // handleCompanySelect once a customer is chosen.
      receivingInstructions: {
        require_serial_numbers_on_receive: false,
        require_mac_addresses_on_receive: false,
        relabel_on_receive: false
      },
      
      // Cross-dock fields
      crossdockPallets: []
    },
    validationSchema: Yup.object({
      requestType: Yup.string().required('Request type is required'),
      companyId: Yup.string().when([], {
        is: () => !isEditing, // Only require company ID when creating new requests
        then: () => Yup.string().required('Company is required'),
        otherwise: () => Yup.string() // Optional for editing
      }),
      scheduledDate: Yup.date()
        .required('Scheduled date is required')
        .nullable()
        .min(new Date(new Date().setHours(0, 0, 0, 0)), 'Expected arrival date cannot be in the past'),
      reference_number: Yup.string().max(100, 'Reference number must be less than 100 characters'),
      vendor: Yup.string().max(255, 'Vendor name must be less than 255 characters'),
      lineItems: Yup.array().when('requestType', {
        is: (requestType) => ['ship', 'pick_pack', 'kit_build'].includes(requestType),
        then: (schema) => schema.min(1, 'At least one item is required'),
        otherwise: (schema) => schema
      }),
      // Validation for incoming inventory requests
      pallets: Yup.array().when('requestType', {
        is: 'incoming',
        then: (schema) => schema.test('has-items', 'At least one pallet or carton is required', function(pallets) {
          const { cartons } = this.parent;
          return (pallets && pallets.length > 0) || (cartons && cartons.length > 0);
        }),
        otherwise: (schema) => schema
      }),
      cartons: Yup.array().when('requestType', {
        is: 'incoming',
        then: (schema) => schema.test('has-items', 'At least one pallet or carton is required', function(cartons) {
          const { pallets } = this.parent;
          return (cartons && cartons.length > 0) || (pallets && pallets.length > 0);
        }),
        otherwise: (schema) => schema
      }),
      destinationWarehouseId: Yup.string().when(['requestType'], {
        is: (requestType) => requestType === 'incoming' && !isEditing,
        then: () => Yup.string().required('Destination warehouse is required'),
        otherwise: () => Yup.string().nullable()
      }),
      specialInstructions: Yup.string(),
      destinationAddress: Yup.object().when(['requestType', 'shippingMethod'], {
        is: (requestType, shippingMethod) => requestType === 'ship' && shippingMethod !== 'pickup',
        then: () => Yup.object({
          street: Yup.string().required('Street address is required'),
          city: Yup.string().required('City is required'),
          state: Yup.string().required('State is required'),
          postalCode: Yup.string().required('Postal code is required'),
          country: Yup.string().required('Country is required')
        }),
        otherwise: () => Yup.object().nullable()
      }),
      shippingMethod: Yup.string().when('requestType', {
        is: (val) => val === 'ship',
        then: () => Yup.string().oneOf(['courier', 'freight', 'pickup']).required('Shipping method is required'),
        otherwise: () => Yup.string().nullable()
      })
    }),
    onSubmit: async (values) => {
      // SAFETY CHECK: Only allow submission on the review step or when editing
      // Shipping requests have 4 steps (review is step 4), others have 3 (review is step 3)
      const isReviewStep = step === 3 || step === 4;
      if (!isReviewStep && !isEditing) {
        console.error('PREVENTING SUBMISSION - Not on review step');
        return; // Prevent submission if not on final step
      }
      
      // Clean up data for submission (remove unnecessary nested data)
      const cleanDataForSubmission = (data) => {
        const cleanedData = { ...data };
        if (cleanedData.requestType === 'ship' && cleanedData.shippingMethod === 'pickup') {
          cleanedData.destinationAddress = null;
          cleanedData.destinationAddressId = '';
          cleanedData.liftgateRequired = false;
          cleanedData.whiteGlove = false;
          cleanedData.inside_delivery = false;
        }
        
        // Clean up pallets - remove backend-specific fields and nested associations
        if (cleanedData.pallets) {
          cleanedData.pallets = cleanedData.pallets.map(pallet => ({
            label: pallet.label,
            type: pallet.type,
            dimensions: pallet.dimensions,
            weight: pallet.weight,
            declaredValue: pallet.declaredValue,
            notes: pallet.notes,
            items: pallet.items,
            productSku: pallet.productSku,
            internalSku: pallet.internalSku,
            productName: pallet.productName,
            quantity: pallet.quantity,
            unitOfMeasure: pallet.unitOfMeasure,
            packagingType: pallet.packagingType,
            cartonCount: pallet.cartonCount,
            unitsPerCarton: pallet.unitsPerCarton,
            cartonDimensions: pallet.cartonDimensions,
            itemDimensions: pallet.itemDimensions,
            individualDimensions: pallet.individualDimensions,
            serialNumbers: pallet.serialNumbers,
            macAddresses: pallet.macAddresses,
            // Operational metadata: dropped here means recreated as
            // null/false by the destructive update.
            projectName: pallet.projectName,
            projectCode: pallet.projectCode,
            specialHandling: pallet.specialHandling,
            storageRequirement: pallet.storageRequirement,
            isHazardous: pallet.isHazardous,
            requested_asset_ids: pallet.requested_asset_ids,
            depalletizeOnArrival: pallet.depalletizeOnArrival,
            crossDock: pallet.crossDock,
            crossDockShippingArrangement: pallet.crossDockShippingArrangement,
            crossDockDestination: pallet.crossDockDestination,
            crossDockShippingDetails: pallet.crossDockShippingDetails,
            crossDockDocuments: pallet.crossDockDocuments,
            cartonInfo: pallet.cartonInfo
            // Remove: id, pallet_label, weight_lbs, PalletItems, etc.
          }));
        }
        
        // Clean up line items - remove unnecessary nested data
        if (cleanedData.lineItems) {
          cleanedData.lineItems = cleanedData.lineItems.map(item => ({
            // Line identity: without it the backend patch treats every
            // submitted line as new and inserts duplicates on each edit.
            id: item.id || item.requestLineItemId || undefined,
            productId: item.productId,
            inventoryItemId: item.inventoryItemId || item.InventoryItemId || item.productId,
            productSku: item.productSku,
            productName: item.productName,
            quantity: item.quantity,
            unitOfMeasure: item.unitOfMeasure,
            notes: item.notes,
            packagingType: item.packagingType,
            weight: item.weight,
            declaredValue: item.declaredValue,
            declared_unit_value: item.declared_unit_value,
            itemDimensions: item.itemDimensions,
            individualDimensions: item.individualDimensions,
            cartonDimensions: item.cartonDimensions,
            specialHandling: item.specialHandling,
            storageRequirement: item.storageRequirement,
            isHazardous: item.isHazardous
            // Remove: id, RequestId, InventoryItemId, Assets, etc.
          }));
        }
        
        return cleanedData;
      };
      
      // Determine whether to create or update based on isEditing flag
      if (isEditing) {
        const cleanedValues = cleanDataForSubmission(values);
        updateRequestMutation.mutate({
          id,
          ...cleanedValues,
        });
      } else {
        const cleanedValues = cleanDataForSubmission(values);
        createRequestMutation.mutate({
          ...cleanedValues
        });
      }
    },
    enableReinitialize: false // Disable auto-reinitialization to prevent unexpected submissions
  });
  
  // Initialize form with existing request data when editing
  useEffect(() => {
    const existingRequestType = getRequestType(existingRequest);
    if (isEditing && existingRequest && existingRequestType && !formik.values.requestType) {
      // Find the company from the existing request
      const company = existingRequest.Company;
      if (company) {
        setSelectedCompany(company);
      }
      
      // Set the selected type
      setSelectedType(existingRequestType === 'ship' && (existingRequest.shippingMethod || existingRequest.shipping_method) === 'pickup'
        ? 'pickup'
        : existingRequestType);
      
      // Set form values from existing request
      formik.setValues({
        requestType: existingRequestType,
        companyId: existingRequest.company_id || existingRequest.companyId || '',
        scheduledDate: existingRequest.scheduledDate || existingRequest.scheduled_date
          ? new Date(existingRequest.scheduledDate || existingRequest.scheduled_date)
          : null,
        lineItems: existingRequest.RequestLineItems || [],
        specialInstructions: existingRequest.specialInstructions || '',
        
        // Set shipping-specific fields
        destinationAddress: existingRequest.destinationAddress || {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: ''
        },
        shippingMethod: existingRequest.shippingMethod || existingRequest.shipping_method || '',
        liftgateRequired: existingRequest.liftgateRequired || existingRequest.liftgate_required || false,
        whiteGlove: existingRequest.whiteGlove || existingRequest.white_glove || false,
        
        // Set other type-specific fields as needed
        sourceAddress: existingRequest.sourceAddress || {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: ''
        },
        kitName: existingRequest.kitName || '',
        kitQuantity: existingRequest.kitQuantity || 1,
        qcChecklist: existingRequest.qcChecklist || [],
        reference_number: existingRequest.reference_number || '',
        vendor: existingRequest.vendor || '',
        // Shared hydration (utils/asnEditHydration): rebuilds editor
        // shapes with packaging, weight, dims, serial selections, and
        // declared-value totals -- the destructive update recreates
        // every line from this state, so anything missing here is
        // erased on save (RF8 review rounds).
        pallets: (() => {
          const hydrated = hydratePalletsFromRequest(existingRequest);
          return (existingRequest.Pallets || []).map((pallet, index) => ({
            ...pallet,
            ...hydrated[index],
            // Keep original fields for backward compatibility
            pallet_label: pallet.pallet_label,
            weight_lbs: pallet.weight_lbs,
            PalletItems: pallet.PalletItems
          }));
        })(),
        // Standalone cartons live as RequestLineItems -- no Cartons
        // collection exists on the API response, so the old lookup left
        // carton-only ASNs uneditable (RF8 review).
        cartons: hydrateCartonsFromRequest(existingRequest),
        destinationWarehouseId: existingRequest.internalWarehouseId || existingRequest.destinationWarehouseId || '',
        // Preserve receiving instructions when editing an existing ASN /
        // manifest so toggles stay consistent across a save cycle.
        receivingInstructions: {
          require_serial_numbers_on_receive:
            existingRequest.receivingInstructions?.require_serial_numbers_on_receive === true
            || existingRequest.receiving_instructions?.require_serial_numbers_on_receive === true,
          require_mac_addresses_on_receive:
            existingRequest.receivingInstructions?.require_mac_addresses_on_receive === true
            || existingRequest.receiving_instructions?.require_mac_addresses_on_receive === true,
          relabel_on_receive:
            existingRequest.receivingInstructions?.relabel_on_receive === true
            || existingRequest.receiving_instructions?.relabel_on_receive === true
        }
      });
      
      // Skip to the form step if editing
      setStep(2);
      setIsLoadingRequest(false);
    }
  }, [isEditing, existingRequest]); // Removed formik.setValues from dependencies
  
  // Handle creating the request
  const createRequestMutation = useMutation(
    (values) => adminAPI.createRequest(values),
    {
      onSuccess: (data) => {
        setSubmitSuccess(true);
        setSubmitError(null);
        
        // Extract the ID properly from the response
        const requestId = data.id || (data.data && data.data.id);
        
        if (!requestId) {
          console.error('No request ID found in response:', data);
          setSubmitError('Request created but could not navigate to details page. Please check the requests list.');
          return;
        }
        
        // Navigate to the request details page after a short delay
        setTimeout(() => {
          navigate(`/admin/requests/${requestId}`);
        }, 1500);
      },
      onError: (error) => {
        console.error('Error creating request:', error);
        setSubmitSuccess(false);
        setSubmitError(error.message || 'Failed to create request. Please try again.');
      }
    }
  );
  
  // Handle updating the request
  const updateRequestMutation = useMutation(
    (values) => adminAPI.updateRequest(values.id, values),
    {
      onSuccess: (data) => {
        setSubmitSuccess(true);
        setSubmitError(null);
        
        // Navigate to the request details page after a short delay
        setTimeout(() => {
          navigate(`/admin/requests/${id}`);
        }, 1500);
      },
      onError: (error) => {
        console.error('Error updating request:', error);
        setSubmitSuccess(false);
        setSubmitError(error.message || 'Failed to update request. Please try again.');
      }
    }
  );
  
  // Handle company selection
  const handleCompanySelect = async (company) => {
    setSelectedCompany(company);
    formik.setFieldValue('companyId', company.id);

    // Pull the selected customer's default receiving preferences and
    // hydrate the ASN / manifest checkboxes so the admin sees the same
    // defaults the customer would see when submitting themselves. If the
    // lookup fails (permissions, network), fall through with the form's
    // empty defaults -- the backend will still apply the company defaults
    // on save via normalizeReceivingInstructions.
    try {
      const detail = await adminAPI.getCustomer(company.id);
      const prefs = detail?.receivingPreferences
        || detail?.data?.receivingPreferences
        || detail?.company?.receiving_preferences
        || null;
      if (prefs && typeof prefs === 'object') {
        formik.setFieldValue('receivingInstructions', {
          require_serial_numbers_on_receive: prefs.require_serial_numbers_on_receive === true,
          require_mac_addresses_on_receive: prefs.require_mac_addresses_on_receive === true,
          relabel_on_receive: prefs.relabel_on_receive === true
        });
      }
    } catch (err) {
      // Non-fatal: admin can still submit and the backend will fill
      // company defaults for any keys the form did not set.
    }
  };
  
  // Handle search term change with debounce
  const handleSearchChange = (e) => {
    setCompanySearchTerm(e.target.value);
  };
  
  // Request type options - filter based on pre-selected type
  const getAllRequestTypes = () => [
    {
      id: 'incoming',
      name: 'Incoming Inventory (ASN)',
      description: 'Receive inventory to warehouse for storage',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707-.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      )
    },
    {
      id: 'inbound_project',
      name: 'Inbound Manifest',
      description: 'Create a manifest for expected incoming inventory (simple BOM/manifest)',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      id: 'ship',
      name: 'Ship Products',
      description: 'Ship products from warehouse to destination',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      )
    },
    {
      id: 'pickup',
      name: 'Pickup',
      description: 'Customer pickup from the selected warehouse',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      )
    },

    {
      id: 'crossdock',
      name: 'Cross-Dock',
      description: 'Temporary storage with fast outbound shipping',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      )
    },
    {
      id: 'kit_build',
      name: 'Kit Building',
      description: 'Assemble components into kits',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      )
    }
  ];

  const requestTypes = (() => {
    const allTypes = getAllRequestTypes();
    
    // Filter based on pre-selected type
    if (preSelectedType === 'incoming') {
      return allTypes.filter(type => ['incoming', 'inbound_project', 'inbound_manifest', 'crossdock'].includes(type.id));
    } else if (preSelectedType === 'incoming,crossdock') {
      return allTypes.filter(type => ['incoming', 'inbound_project', 'inbound_manifest', 'crossdock'].includes(type.id));
    } else if (preSelectedType === 'ship') {
      return allTypes.filter(type => ['ship', 'pickup', 'kit_build'].includes(type.id));
    } else {
      return allTypes; // Show all types for "All Requests" or no filter
    }
  })();
  
  // Handle request type selection
  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
    if (typeId === 'pickup') {
      formik.setFieldValue('requestType', 'ship');
      formik.setFieldValue('shippingMethod', 'pickup');
      return;
    }

    formik.setFieldValue('requestType', typeId);
    if (typeId === 'ship' && formik.values.shippingMethod === 'pickup') {
      formik.setFieldValue('shippingMethod', '');
    }
  };
  
  // Handle step changes
  const goToNextStep = async () => {
    if (step === 1) {
      if (!formik.values.requestType) {
        formik.setFieldError('requestType', 'Please select a request type');
        return;
      }
      
      if (!formik.values.companyId) {
        formik.setFieldError('companyId', 'Please select a company');
        return;
      }
      
      // Special handling for inbound manifest - redirect to dedicated create page
      if (formik.values.requestType === 'inbound_project') {
        navigate(`/admin/inbound-projects/new?companyId=${formik.values.companyId}`);
        return;
      }
    }
    
    // For incoming requests, validate ASN before proceeding to review
    if (formik.values.requestType === 'incoming' && step === 2) {
      if (asnValidationFunction && typeof asnValidationFunction === 'function') {
        try {
          const validationResults = await asnValidationFunction();
          if (!validationResults.isValid) {
            // Show validation errors
            const errorMessages = validationResults.errors.map(error => error.message).join('\n');
            alert(`Please resolve the following validation issues before continuing:\n\n${errorMessages}`);
            return;
          }
        } catch (error) {
          console.error('Validation failed:', error);
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name
          });
          alert(`Validation failed: ${error.message || 'Unknown error'}. Please check your ASN and try again.`);
          return;
        }
      }
    }
    
    // For carrier shipping requests, navigate to shipping options after step 2.
    // Pickup requests go directly to review.
    if (formik.values.requestType === 'ship' && step === 2 && formik.values.shippingMethod !== 'pickup') {
      // Calculate totals from line items for shipping rates
      const weight = formik.values.lineItems.reduce((sum, item) => 
        sum + (parseFloat(item.weight || 5) * parseFloat(item.quantity || 0)), 0);
      const volume = formik.values.lineItems.reduce((sum, item) => 
        sum + (parseFloat(item.volume || 1) * parseFloat(item.quantity || 0)), 0);
      
      // Navigate to shipping options page with shipment data
      navigate('/admin/requests/shipping/options', {
        state: {
          shipmentData: {
            ...formik.values,
            weight: weight || 25, // Default weight if not specified
            volume: volume || 5,  // Default volume if not specified
            _selectedCompany: selectedCompany, // Preserve company selection through navigation
          },
          fromStep: step
        }
      });
      return;
    }
    
    setStep(step + 1);
  };
  
  const goToPreviousStep = () => {
    setStep(step - 1);
  };
  
  // Define step mappings by request type
  const stepsByType = {
    shipping: formik.values.shippingMethod === 'pickup'
      ? ['Type', 'Details', 'Review']
      : ['Type', 'Details', 'Shipping Options', 'Review'],
    asn: ['Type', 'Details', 'Review'],
    crossdock: ['Type', 'Details', 'Review'],
    kit: ['Type', 'Details', 'Review']
  };

  // Get step configuration based on request type
  const getStepConfig = () => {
    const requestType = formik.values.requestType;
    
    // If no request type selected, show only step 1
    if (!requestType) {
      return {
        steps: [{ id: 1, name: 'Select Company & Type', shortName: 'Type' }],
        totalSteps: 1
      };
    }
    
    // Map request types to step configuration keys
    const typeMapping = {
      'ship': 'shipping',
      'incoming': 'asn',
      'crossdock': 'crossdock',
      'kit_build': 'kit'
    };
    
    // Get steps for the selected request type
    const stepConfigKey = typeMapping[requestType] || 'asn';
    const stepLabels = stepsByType[stepConfigKey];
    const steps = stepLabels.map((label, index) => ({
      id: index + 1,
      name: label === 'Type' ? 'Select Company & Type' : label,
      shortName: label
    }));
    
    return {
      steps,
      totalSteps: steps.length
    };
  };

  const stepConfig = getStepConfig();
  const isManifestEdit = isEditing && formik.values.requestType === 'inbound_manifest';
  
  // Render form based on selected type
  const renderForm = () => {
    switch (formik.values.requestType) {
      case 'incoming':
        return (
          <IncomingInventoryForm 
            formik={formik} 
            setSelectedDestinationWarehouseName={setSelectedDestinationWarehouseName}
            onValidationRequest={setAsnValidationFunction}
            isAdmin={true}
          />
        );
      case 'ship':
        return <ShippingForm formik={formik} />;
      case 'inbound_manifest':
        return (
          <ManifestEditForm
            requestId={id}
            api={adminAPI}
            onCancel={() => navigate(`/admin/requests/${id}`)}
            onSaved={() => navigate(`/admin/requests/${id}`)}
          />
        );
      case 'crossdock':
        return <CrossDockForm formik={formik} />;
      case 'kit_build':
        return <KitBuildForm formik={formik} />;
      default:
        return <div>Please select a request type</div>;
    }
  };
  
  if (isEditing && !hasValidId) {
    return <ErrorAlert message="Invalid request ID. Please go back to the request detail page and try again." />;
  }

  // Show error if failed to load request
  if (isEditing && (loadingError || existingRequestError)) {
    return <ErrorAlert message={loadingError || existingRequestError?.message || 'Failed to load request'} />;
  }

  if (isEditing && !isLoadingExistingRequest && (!existingRequest || !getRequestType(existingRequest))) {
    return <ErrorAlert message="Request could not be loaded for editing. Please go back to the request detail page and try again." />;
  }

  // Show loading spinner if fetching or hydrating an existing request
  if (isEditing && (isLoadingRequest || isLoadingExistingRequest || !formik.values.requestType)) {
    return <LoadingSpinner />;
  }
  
  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEditing ? 'Edit Request' : 'Create New Request'}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {isEditing 
              ? 'Edit the service request details'
              : 'Create a new service request on behalf of a company'
            }
          </p>
        </div>
        
        {/* Progress Steps - Only shown in create mode */}
        {!isEditing && stepConfig.steps.length > 0 && (
          <StepProgress steps={stepConfig.steps} currentStep={step} />
        )}
        
        <form onSubmit={(e) => {
          // Only allow submission on review step or when editing
          const isReviewStep = step === stepConfig.totalSteps;
          if (!isReviewStep && !isEditing) {
            console.error('BLOCKING FORM SUBMISSION - Wrong step');
            e.preventDefault();
            e.stopPropagation();
            return false;
          }

          // Let formik handle the actual submission
          formik.handleSubmit(e);
        }} onKeyDown={(e) => {
          // Prevent Enter key submission unless on final step
          if (e.key === 'Enter' && step !== stepConfig.totalSteps) {
            e.preventDefault();
          }
        }}>
          {/* Step 1: Select Company and Request Type */}
          {step === 1 && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                {/* Company Selection */}
                <div className="mb-8">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Select Company</h2>
                  
                  <div className="mb-4">
                    <label htmlFor="companySearch" className="block text-sm font-medium text-gray-700">
                      Search Companies
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="text"
                        id="companySearch"
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="Search by company name, account number..."
                        value={companySearchTerm}
                        onChange={handleSearchChange}
                      />
                    </div>
                  </div>
                  
                  {/* Selected Company Display */}
                  {selectedCompany && (
                    <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-indigo-900">
                            Selected: {selectedCompany.name || selectedCompany.companyName}
                          </h3>
                          {selectedCompany.accountNumber && (
                            <p className="text-xs text-indigo-700">Account: {selectedCompany.accountNumber}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCompany(null);
                            formik.setFieldValue('companyId', '');
                          }}
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Company Search Results */}
                  {!selectedCompany && companySearchTerm.trim().length >= 3 && (
                    <div className="bg-gray-50 p-4 rounded-md max-h-60 overflow-y-auto">
                      {isLoadingCompanies ? (
                        <p className="text-gray-500 text-sm">Loading companies...</p>
                      ) : (processedCompanies.length > 0) ? (
                        <div className="space-y-2">
                          {processedCompanies.map((company) => (
                            <div
                              key={company.id}
                              className="p-3 rounded-md cursor-pointer bg-white border border-gray-200 hover:bg-gray-50"
                              onClick={() => handleCompanySelect(company)}
                            >
                              <div className="flex justify-between">
                                <h3 className="text-sm font-medium text-gray-900">{company.name || company.companyName}</h3>
                              </div>
                              {company.accountNumber && (
                                <p className="text-xs text-gray-500">Account: {company.accountNumber}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">
                          No companies found. Try a different search term.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {!selectedCompany && companySearchTerm.trim().length < 3 && (
                    <div className="bg-gray-50 p-4 rounded-md">
                      <p className="text-gray-500 text-sm">
                        Start typing at least 3 characters to search for companies.
                      </p>
                    </div>
                  )}
                  
                  {formik.errors.companyId && formik.touched.companyId && (
                    <p className="mt-2 text-sm text-red-600">{formik.errors.companyId}</p>
                  )}
                </div>
                
                {/* Request Type Selection */}
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Select Request Type</h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {requestTypes.map((type) => (
                      <div
                        key={type.id}
                        className={`relative rounded-lg border p-4 cursor-pointer hover:bg-gray-50 focus:outline-none ${
                          selectedType === type.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'
                        }`}
                        onClick={() => handleTypeSelect(type.id)}
                      >
                        <div className="flex items-start">
                          <div className={`flex-shrink-0 ${selectedType === type.id ? 'text-indigo-600' : 'text-gray-400'}`}>
                            {type.icon}
                          </div>
                          <div className="ml-3">
                            <h3 className={`text-sm font-medium ${selectedType === type.id ? 'text-indigo-900' : 'text-gray-900'}`}>
                              {type.name}
                            </h3>
                            <p className={`mt-1 text-sm ${selectedType === type.id ? 'text-indigo-700' : 'text-gray-500'}`}>
                              {type.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {formik.errors.requestType && formik.touched.requestType && (
                    <p className="mt-2 text-sm text-red-600">{formik.errors.requestType}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Step 2: Request Details */}
          {step === 2 && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                <div className="pb-4 mb-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">
                    {formik.values.requestType === 'incoming' && 'Incoming Inventory Details'}
                    {formik.values.requestType === 'ship' && 'Shipping Request Details'}
                    {formik.values.requestType === 'crossdock' && 'Cross-Dock Request Details'}
                    {formik.values.requestType === 'kit_build' && 'Kit Building Request Details'}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {isEditing ? 'Editing request' : 'Creating request'} for {selectedCompany?.name || selectedCompany?.companyName || 'selected company'}
                  </p>
                </div>
                {renderForm()}
              </div>
            </div>
          )}
          
          {/* Step 3: Review Request */}
          {step === stepConfig.totalSteps && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Review Request</h2>
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Ready to Create Request
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>Please review all the details below. When you're ready, click the "Create Request" button to submit this request.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <dl className="divide-y divide-gray-200">
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Company</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {selectedCompany?.name || selectedCompany?.companyName || 'N/A'}
                      </dd>
                    </div>
                    
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Request Type</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {formik.values.requestType === 'incoming' && 'Incoming Inventory'}
                        {formik.values.requestType === 'ship' && (formik.values.shippingMethod === 'pickup' ? 'Pickup' : 'Ship Products')}
                        {formik.values.requestType === 'crossdock' && 'Cross-Dock'}
                        {formik.values.requestType === 'pick_pack' && 'Pick & Pack'}
                        {formik.values.requestType === 'kit_build' && 'Kit Building'}
                      </dd>
                    </div>

                    {formik.values.requestType === 'ship' && (
                      <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Outbound Type</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {formik.values.shippingMethod === 'pickup' ? 'Pickup' : 'Ship'}
                        </dd>
                      </div>
                    )}
                    
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">
                        {formik.values.requestType === 'ship' && formik.values.shippingMethod === 'pickup'
                          ? 'Expected Pickup Date'
                          : 'Scheduled Date'}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {formik.values.scheduledDate ? formik.values.scheduledDate.toLocaleDateString() : 'Not set'}
                      </dd>
                    </div>
                    
                    {formik.values.reference_number && (
                      <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Reference Number</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {formik.values.reference_number}
                        </dd>
                      </div>
                    )}
                    
                    {formik.values.vendor && (
                      <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Vendor</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {formik.values.vendor}
                        </dd>
                      </div>
                    )}
                    
                    {/* Only show Items section for non-crossdock requests */}
                    {formik.values.requestType !== 'crossdock' && (
                      <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Items</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {/* Display items based on request type */}
                          {formik.values.requestType === 'incoming' ? (
                          <div className="space-y-4">
                            {/* Display Pallets */}
                            {formik.values.pallets && formik.values.pallets.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Pallets ({formik.values.pallets.length})</h4>
                                <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                                  {formik.values.pallets.map((pallet, index) => (
                                    <div key={index} className="p-3">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-sm">{pallet.label}</span>
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          pallet.type === 'single' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                        }`}>
                                          {pallet.type === 'single' ? 'Single SKU' : 'Multi SKU'}
                                        </span>
                                      </div>
                                      
                                      {pallet.type === 'single' ? (
                                        <div className="text-sm text-gray-600">
                                          <p>{pallet.productName} ({pallet.productSku})</p>
                                          <p>Qty: {pallet.quantity} {pallet.unitOfMeasure}</p>
                                        </div>
                                      ) : (
                                        <div className="text-sm text-gray-600">
                                          <p>{pallet.items?.length || 0} different items</p>
                                          {pallet.items && pallet.items.length > 0 && (
                                            <ul className="mt-1 ml-4 space-y-1">
                                              {pallet.items.slice(0, 3).map((item, itemIndex) => (
                                                <li key={itemIndex} className="text-xs">
                                                  • {item.productName} ({item.productSku}) - 
                                                  {item.packagingType === 'carton' 
                                                    ? ` ${item.cartonCount} cartons × ${item.unitsPerCarton} ${item.unitOfMeasure}`
                                                    : ` Qty: ${item.quantity} ${item.unitOfMeasure}`
                                                  }
                                                </li>
                                              ))}
                                              {pallet.items.length > 3 && (
                                                <li className="text-xs text-gray-500">
                                                  ... and {pallet.items.length - 3} more items
                                                </li>
                                              )}
                                            </ul>
                                          )}
                                        </div>
                                      )}
                                      
                                      {(pallet.dimensions?.length || pallet.dimensions?.width || pallet.dimensions?.height || pallet.weight) && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          Dimensions: {pallet.dimensions.length || 0}×{pallet.dimensions.width || 0}×{pallet.dimensions.height || 0}" 
                                          {pallet.weight && `, ${pallet.weight} lbs`}
                                        </div>
                                      )}
                                      
                                      {/* Show warehouse instructions */}
                                      {(pallet.depalletizeOnArrival || pallet.crossDock) && (
                                        <div className="flex gap-2 mt-2">
                                          {pallet.depalletizeOnArrival && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                              <LuBox className="h-3.5 w-3.5 mr-1" />
                                              Depalletize
                                            </span>
                                          )}
                                          {pallet.crossDock && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                              Cross-dock
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Display Cartons */}
                            {formik.values.cartons && formik.values.cartons.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Cartons ({formik.values.cartons.length})</h4>
                                <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                                  {formik.values.cartons.map((carton, index) => (
                                    <div key={index} className="p-3">
                                      <div className="text-sm">
                                        <p className="font-medium">{carton.productName} ({carton.productSku})</p>
                                        <p className="text-gray-600">Qty: {carton.quantity} {carton.unitOfMeasure}</p>
                                        {(carton.dimensions?.length || carton.dimensions?.width || carton.dimensions?.height || carton.dimensions?.weight) && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            Dimensions: {carton.dimensions.length || 0}×{carton.dimensions.width || 0}×{carton.dimensions.height || 0}"
                                            {carton.dimensions.weight && `, ${carton.dimensions.weight} lbs`}
                                          </p>
                                        )}
                                        {carton.serialNumbers && carton.serialNumbers.length > 0 && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            Serial Numbers: {carton.serialNumbers.length} provided
                                          </p>
                                        )}
                                        {carton.macAddresses && carton.macAddresses.length > 0 && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            MAC Addresses: {carton.macAddresses.length} provided
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Show message if no items */}
                            {(!formik.values.pallets || formik.values.pallets.length === 0) && 
                             (!formik.values.cartons || formik.values.cartons.length === 0) && (
                              <div className="text-sm text-gray-500 italic">
                                No items added yet
                              </div>
                            )}
                          </div>
                        ) : (
                          /* For other request types, show lineItems as before */
                          <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                            {formik.values.lineItems && formik.values.lineItems.length > 0 ? (
                              formik.values.lineItems.map((item, index) => (
                                <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                                  <div className="w-0 flex-1 flex items-center">
                                    <span className="ml-2 flex-1 w-0 truncate">
                                      {item.productName} (x{item.quantity})
                                    </span>
                                  </div>
                                </li>
                              ))
                            ) : (
                              <li className="pl-3 pr-4 py-3 text-sm text-gray-500 italic">
                                No items added yet
                              </li>
                            )}
                          </ul>
                        )}
                      </dd>
                    </div>
                    )}
                    
                    {formik.values.specialInstructions && (
                      <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Special Instructions</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {formik.values.specialInstructions}
                        </dd>
                      </div>
                    )}
                    
                    {/* Request-specific fields */}
                    {formik.values.requestType === 'ship' && (
                      <>
                        {formik.values.shippingMethod !== 'pickup' && (
                          <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Destination Address</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                              <address className="not-italic">
                                {formik.values.destinationAddress.street}<br />
                                {formik.values.destinationAddress.city}, {formik.values.destinationAddress.state} {formik.values.destinationAddress.postalCode}<br />
                                {formik.values.destinationAddress.country}
                              </address>
                            </dd>
                          </div>
                        )}
                        
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                          <dt className="text-sm font-medium text-gray-500">Shipping Method</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {formatShippingMethod(formik.values.shippingMethod)}
                          </dd>
                        </div>
                        
                        {(formik.values.liftgateRequired || formik.values.whiteGlove) && (
                          <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Special Services</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                              {formik.values.liftgateRequired && <div>Liftgate Required</div>}
                              {formik.values.whiteGlove && <div>White Glove Service</div>}
                            </dd>
                          </div>
                        )}
                      </>
                    )}
                    
                    {formik.values.requestType === 'kit_build' && formik.values.kitName && (
                      <>
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                          <dt className="text-sm font-medium text-gray-500">Kit Name</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {formik.values.kitName}
                          </dd>
                        </div>
                        
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                          <dt className="text-sm font-medium text-gray-500">Kit Quantity</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {formik.values.kitQuantity}
                          </dd>
                        </div>
                      </>
                    )}
                    
                    {formik.values.requestType === 'incoming' && formik.values.destinationWarehouseId && (
                      <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Destination Warehouse</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {selectedDestinationWarehouseName || 'Selected warehouse'}
                        </dd>
                      </div>
                    )}
                    
                    {/* Cross-dock specific review section */}
                    {formik.values.requestType === 'crossdock' && (
                      <>
                        {/* Cross-dock Pallets */}
                        {formik.values.crossdockPallets && formik.values.crossdockPallets.length > 0 && (
                          <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Cross-dock Pallets</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                              <div className="space-y-3">
                                {formik.values.crossdockPallets.map((pallet, index) => (
                                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-medium text-sm">{pallet.palletLabel}</span>
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        Qty: {pallet.palletQuantity || 1}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1">
                                      <p><span className="font-medium">Commodity:</span> {pallet.commodity}</p>
                                      <p><span className="font-medium">Dimensions:</span> {pallet.length}×{pallet.width}×{pallet.height}" ({pallet.grossWeight} lbs)</p>
                                      {pallet.notes && (
                                        <p><span className="font-medium">Notes:</span> {pallet.notes}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                <div className="text-xs text-gray-500 mt-2">
                                  Total Pallets: {formik.values.crossdockPallets.reduce((sum, p) => sum + (parseInt(p.palletQuantity) || 1), 0)}
                                </div>
                              </div>
                            </dd>
                          </div>
                        )}
                        
                        {/* Cross-dock Configuration */}
                        {formik.values.crossdockData && (
                          <>
                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                              <dt className="text-sm font-medium text-gray-500">Expected Dates</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <div className="space-y-1">
                                  {formik.values.crossdockData.expectedArrivalDate && (
                                    <p><span className="font-medium">Arrival:</span> {new Date(formik.values.crossdockData.expectedArrivalDate).toLocaleDateString()}</p>
                                  )}
                                  {formik.values.crossdockData.expectedPickupDate && (
                                    <p><span className="font-medium">Pickup:</span> {new Date(formik.values.crossdockData.expectedPickupDate).toLocaleDateString()}</p>
                                  )}
                                </div>
                              </dd>
                            </div>
                            
                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                              <dt className="text-sm font-medium text-gray-500">Destination Warehouse</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {selectedDestinationWarehouseName || 'Selected warehouse'}
                              </dd>
                            </div>
                            
                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                              <dt className="text-sm font-medium text-gray-500">Shipping Arrangement</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <div className="space-y-2">
                                  <p className="font-medium">
                                    {formik.values.crossdockData.shippingArrangement === 'customer'
                                      ? 'Customer will arrange shipping'
                                      : 'Warehouse will arrange shipping'
                                    }
                                  </p>
                                  
                                  {/* Customer arranges - show documents */}
                                  {formik.values.crossdockData.shippingArrangement === 'customer' && (
                                    <div className="bg-blue-50 p-3 rounded-md">
                                      <p className="text-sm font-medium text-blue-800">Shipping Documents:</p>
                                      {formik.values.crossdockData.documentsProvidedNow ? (
                                        <div className="text-sm text-blue-700 mt-1 space-y-1">
                                          {formik.values.crossdockData.documents?.bolNumber && (
                                            <p>BOL: {formik.values.crossdockData.documents.bolNumber}</p>
                                          )}
                                          {formik.values.crossdockData.documents?.trackingNumber && (
                                            <p>Tracking: {formik.values.crossdockData.documents.trackingNumber}</p>
                                          )}
                                          {formik.values.crossdockData.documents?.notes && (
                                            <p>Notes: {formik.values.crossdockData.documents.notes}</p>
                                          )}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-blue-700 mt-1">Will provide documents later</p>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Warehouse arranges - show destination and requirements */}
                                  {formik.values.crossdockData.shippingArrangement === 'warehouse' && (
                                    <div className="p-3 rounded-md space-y-2 border border-gray-200">
                                      <p className="text-sm font-medium text-gray-800">Final Destination:</p>
                                      {formik.values.crossdockData.crossDockDestinationType === 'existing' && formik.values.crossdockData.selectedDestinationDetails ? (
                                        <div className="text-sm text-gray-700">
                                          <p>{formik.values.crossdockData.selectedDestinationDetails.name}</p>
                                          <p>{formik.values.crossdockData.selectedDestinationDetails.street_address}</p>
                                          {formik.values.crossdockData.selectedDestinationDetails.address_line_2 && (
                                            <p>{formik.values.crossdockData.selectedDestinationDetails.address_line_2}</p>
                                          )}
                                          <p>{formik.values.crossdockData.selectedDestinationDetails.city}, {formik.values.crossdockData.selectedDestinationDetails.state_province} {formik.values.crossdockData.selectedDestinationDetails.postal_code}</p>
                                          <p>{formik.values.crossdockData.selectedDestinationDetails.country}</p>
                                          {formik.values.crossdockData.selectedDestinationDetails.contact_name && (
                                            <p>Contact: {formik.values.crossdockData.selectedDestinationDetails.contact_name}</p>
                                          )}
                                          {formik.values.crossdockData.selectedDestinationDetails.contact_phone && (
                                            <p>Phone: {formik.values.crossdockData.selectedDestinationDetails.contact_phone}</p>
                                          )}
                                        </div>
                                      ) : formik.values.crossdockData.crossDockDestination && (
                                        <div className="text-sm text-gray-700">
                                          <p>{formik.values.crossdockData.crossDockDestination.name}</p>
                                          <p>{formik.values.crossdockData.crossDockDestination.street_address}</p>
                                          {formik.values.crossdockData.crossDockDestination.address_line_2 && (
                                            <p>{formik.values.crossdockData.crossDockDestination.address_line_2}</p>
                                          )}
                                          <p>{formik.values.crossdockData.crossDockDestination.city}, {formik.values.crossdockData.crossDockDestination.state_province} {formik.values.crossdockData.crossDockDestination.postal_code}</p>
                                          <p>{formik.values.crossdockData.crossDockDestination.country}</p>
                                          {formik.values.crossdockData.crossDockDestination.contact_name && (
                                            <p>Contact: {formik.values.crossdockData.crossDockDestination.contact_name}</p>
                                          )}
                                          {formik.values.crossdockData.crossDockDestination.contact_phone && (
                                            <p>Phone: {formik.values.crossdockData.crossDockDestination.contact_phone}</p>
                                          )}
                                        </div>
                                      )}
                                      
                                      {formik.values.crossdockData.facilityType && (
                                        <p className="text-sm text-gray-700">
                                          <span className="font-medium">Facility Type:</span> {formik.values.crossdockData.facilityType.charAt(0).toUpperCase() + formik.values.crossdockData.facilityType.slice(1)}
                                        </p>
                                      )}

                                      {(formik.values.crossdockData.siteContactName || formik.values.crossdockData.siteContactPhone) && (
                                        <div className="text-sm text-gray-700">
                                          <p className="font-medium">Site Contact:</p>
                                          {formik.values.crossdockData.siteContactName && (
                                            <p>Name: {formik.values.crossdockData.siteContactName}</p>
                                          )}
                                          {formik.values.crossdockData.siteContactPhone && (
                                            <p>Phone: {formik.values.crossdockData.siteContactPhone}</p>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Delivery Requirements */}
                                      {formik.values.crossdockData.deliveryRequirements && Object.values(formik.values.crossdockData.deliveryRequirements).some(req => req) && (
                                        <div className="text-sm text-gray-700">
                                          <p className="font-medium">Delivery Requirements:</p>
                                          <ul className="mt-1 list-disc list-inside">
                                            {formik.values.crossdockData.deliveryRequirements.liftgateRequired && (
                                              <li>Liftgate Required</li>
                                            )}
                                            {formik.values.crossdockData.deliveryRequirements.whiteGloveService && (
                                              <li>White Glove Service</li>
                                            )}
                                            {formik.values.crossdockData.deliveryRequirements.insideDelivery && (
                                              <li>Inside Delivery</li>
                                            )}
                                            {formik.values.crossdockData.deliveryRequirements.appointmentRequired && (
                                              <li>Appointment Required</li>
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </dd>
                            </div>
                            
                            {formik.values.crossdockData.specialInstructions && (
                              <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                                <dt className="text-sm font-medium text-gray-500">Special Instructions</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                  <div className="bg-yellow-50 p-3 rounded-md">
                                    <p className="text-sm text-yellow-800">{formik.values.crossdockData.specialInstructions}</p>
                                  </div>
                                </dd>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          )}
          
          {/* Form Navigation */}
          {!isManifestEdit && (
            <div className="mt-8 flex justify-between">
              {step > 1 && !isEditing ? (
                <button
                  type="button"
                  onClick={goToPreviousStep}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Back
                </button>
              ) : isEditing ? (
                <button
                  type="button"
                  onClick={() => navigate(`/admin/requests/${id}`)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
              ) : (
                <div></div> // Empty div to maintain layout
              )}

              {step < stepConfig.totalSteps && !isEditing ? (
                <button
                  type="button"
                  onClick={goToNextStep}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Continue
                </button>
              ) : (
                <button
                  type="button"
                  disabled={createRequestMutation.isLoading || (isEditing && updateRequestMutation.isLoading)}
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    // Validate first to show errors in console for debugging
                    const errors = await formik.validateForm();
                    if (Object.keys(errors).length > 0) {
                      console.warn('Validation errors:', errors);
                      // Set all error fields as touched so error messages show
                      const touchedFields = {};
                      Object.keys(errors).forEach(key => {
                        if (typeof errors[key] === 'object' && errors[key] !== null && !Array.isArray(errors[key])) {
                          // Handle nested objects like destinationAddress
                          touchedFields[key] = {};
                          Object.keys(errors[key]).forEach(subKey => {
                            touchedFields[key][subKey] = true;
                          });
                        } else {
                          touchedFields[key] = true;
                        }
                      });
                      formik.setTouched(touchedFields);
                      return;
                    }

                    // Submit - formik.submitForm() validates again internally
                    formik.submitForm();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {isEditing
                    ? (updateRequestMutation.isLoading ? 'Saving...' : 'Save Changes')
                    : (createRequestMutation.isLoading ? 'Creating...' : 'Create Request')
                  }
                </button>
              )}
            </div>
          )}
          
          {submitError && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              <p>{submitError}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AdminCreateRequest; 
